import { getApiBaseUrl, getTenantSubdomain } from '../config/api';
import { notifyUnauthorized } from './authSession';
import { loginHrefForCurrentLocation } from '../utils/auth-return';

const DEBUG = (import.meta as any).env?.VITE_DEBUG_API === 'true';

// Lazy-load toast to avoid circular deps and support SSR
const showErrorToast = (message: string) => {
  import('react-hot-toast').then(({ default: toast }) => toast.error(message));
};

// Helper function to check if data is FormData
const isFormData = (data: any): data is FormData => {
  return data && 
    typeof data === 'object' && 
    (data instanceof FormData || 
     data.constructor?.name === 'FormData' ||
     (typeof data.append === 'function' && typeof data.get === 'function'));
};

// Helper function to convert Date objects to ISO strings
const convertDatesToISOStrings = (data: any): any => {
  if (isFormData(data)) return data;
  
  if (data instanceof Date) {
    return data.toISOString();
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertDatesToISOStrings(item));
  }
  
  if (data && typeof data === 'object') {
    const result: any = {};
    for (const key in data) {
      result[key] = convertDatesToISOStrings(data[key]);
    }
    return result;
  }
  
  return data;
};

// Define API response type to provide consistent structure
interface ApiResponse<T> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
}

const DEFAULT_TIMEOUT_MS = Number((import.meta as any).env?.VITE_API_TIMEOUT_MS) || 60_000;
const TENANT_WAKE_ATTEMPTS = 5;

const isWakeNetworkError = (error: unknown): boolean => {
  const err = error as { name?: string; message?: string } | undefined;
  if (!err) return false;
  if (err.name === 'AbortError' || err.name === 'TypeError') return true;
  return /fetch|network|failed|timeout|abort|connection/i.test(err.message || '');
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Global fetch API timeout handler. On purchased hosts, retry while Fly autostop wakes.
const fetchWithTimeout = async (resource: string, options: RequestInit & { timeout?: number } = {}): Promise<Response> => {
  const { timeout = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
  const attempts = getTenantSubdomain() ? TENANT_WAKE_ATTEMPTS : 1;
  let lastError: unknown;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(resource, {
        ...fetchOptions,
        signal: controller.signal
      });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      lastError = error;
      if (attempt < attempts - 1 && isWakeNetworkError(error)) {
        await delay(2000 * (attempt + 1));
        continue;
      }
      if ((error as Error).name === 'AbortError') {
        throw new Error(
          getTenantSubdomain()
            ? 'Your instance is starting. Please try again.'
            : 'Request timed out: Please try again',
        );
      }
      throw error;
    }
  }

  if ((lastError as Error)?.name === 'AbortError') {
    throw new Error('Your instance is starting. Please try again.');
  }
  throw lastError;
};

// Helper function to get auth headers
const getAuthHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token && token.trim() !== '') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Base API configuration
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

// Flag to prevent multiple redirects
let isRedirecting = false;

// Cache for preventing duplicate requests
const pendingRequests = new Map<string, Promise<any>>();

// Short-lived cache for GET requests (5 seconds)
const responseCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 5000; // 5 seconds

const invalidateGetCache = (): void => {
  responseCache.clear();
};

// Helper function to create a cache key
const createCacheKey = (method: string, url: string, params?: Record<string, string>, data?: any): string => {
  const paramsStr = params ? JSON.stringify(params) : '';
  const dataStr = data ? JSON.stringify(data) : '';
  return `${method}:${url}:${paramsStr}:${dataStr}`;
};

// Helper function to normalize URLs
const normalizeUrl = (baseUrl: string, path: string): string => {
  // Remove trailing slash from base URL if present
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // Remove leading slash from path if present
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Join with a single slash
  return `${normalizedBase}/${normalizedPath}`;
};

// Create API client with similar interface to Axios
const api = {
  async get<T>(url: string, params?: Record<string, string>): Promise<ApiResponse<T>> {
    const cacheKey = createCacheKey('GET', url, params);
    
    // Check for cached response (short-lived cache for GET requests)
    const cached = responseCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }
    
    const timestamp = new Date().toISOString();
    const queryParams = params ? '?' + new URLSearchParams(params).toString() : '';
    
    const fullUrl = normalizeUrl(getApiBaseUrl(), url) + queryParams;
    const headers = getAuthHeaders();
    
    if (DEBUG) console.debug('[API] GET', url, params);
    
    // Create the request promise
    const requestPromise = (async () => {
      try {
        const response = await fetchWithTimeout(fullUrl, {
          method: 'GET',
          headers,
        });
        
        // Handle 204 No Content responses (empty body)
        let data = null;
        if (response.status !== 204) {
          const contentType = response.headers.get('content-type');
          const contentLength = response.headers.get('content-length');
          
          // Read response as text first (can only read once)
          const text = await response.text();
          
          // Only parse if we have content. Prefer JSON whenever the body looks
          // like JSON — some proxies omit/alter Content-Type on large payloads.
          if (text && text.trim()) {
            const trimmed = text.trim();
            const looksLikeJson =
              (contentType && contentType.includes('application/json')) ||
              trimmed.startsWith('{') ||
              trimmed.startsWith('[');
            if (looksLikeJson) {
              try {
                data = JSON.parse(text);
              } catch (parseError) {
                if (DEBUG) console.warn('Failed to parse JSON:', parseError);
                data = text;
              }
            } else {
              data = text;
            }
          }
        }
        
        if (DEBUG) console.debug('[API] GET Response', url, response.status);
        
        if (!response.ok) {
          throw {
            status: response.status,
            statusText: response.statusText,
            data,
            message: `Request failed with status code ${response.status}`,
            url,
          };
        }
        
        const result = {
          data,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        };
        
        // Cache successful GET responses
        responseCache.set(cacheKey, { data: result, timestamp: Date.now() });
        
        return result;
      } catch (error) {
        handleApiError(error, timestamp);
        throw error;
      } finally {
        // Remove from cache when request completes (success or failure)
        pendingRequests.delete(cacheKey);
      }
    })();
    
    // Store the promise in cache
    pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  },
  
  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return makeRequest<T>('POST', url, data);
  },
  
  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return makeRequest<T>('PATCH', url, data);
  },
  
  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    return makeRequest<T>('PUT', url, data);
  },
  
  async delete<T>(url: string): Promise<ApiResponse<T>> {
    return makeRequest<T>('DELETE', url);
  },

  // Special method for AI scraping with longer timeout
  async postWithTimeout<T>(url: string, data?: any, timeout: number = 60000): Promise<ApiResponse<T>> {
    const cacheKey = createCacheKey('POST', url, undefined, data);
    
    // Check if there's already a pending request for this exact same call
    if (pendingRequests.has(cacheKey)) {
      return pendingRequests.get(cacheKey);
    }
    
    const timestamp = new Date().toISOString();
    const fullUrl = normalizeUrl(getApiBaseUrl(), url);
    const headers = getAuthHeaders();
    
    // Set Content-Type only for non-FormData requests
    if (data && !(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (DEBUG) console.debug('[API] POST', url);
    
    const requestPromise = (async () => {
      try {
        const response = await fetchWithTimeout(fullUrl, {
          method: 'POST',
          headers,
          body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
          timeout,
        });
        
        // Handle 204 No Content responses (empty body)
        let responseData = null;
        if (response.status !== 204) {
          const contentType = response.headers.get('content-type');
          
          // Read response as text first (can only read once)
          const text = await response.text();
          
          // Only parse if we have content
          if (text && text.trim()) {
            if (contentType && contentType.includes('application/json')) {
              try {
                responseData = JSON.parse(text);
              } catch (parseError) {
                if (DEBUG) console.warn('Failed to parse JSON:', parseError);
                responseData = text;
              }
            } else {
              responseData = text;
            }
          }
        }
        
        if (DEBUG) console.debug('[API] POST Response', url, response.status);
        
        if (!response.ok) {
          throw {
            status: response.status,
            statusText: response.statusText,
            data: responseData,
            message: `Request failed with status code ${response.status}`,
            url,
          };
        }

        invalidateGetCache();
        
        return {
          data: responseData as T,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        };
      } catch (error) {
        handleApiError(error, timestamp);
        throw error;
      } finally {
        // Remove from cache when request completes (success or failure)
        pendingRequests.delete(cacheKey);
      }
    })();
    
    // Store the promise in cache
    pendingRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }
};

// Helper function to make requests with body
const makeRequest = async <T>(method: string, url: string, data?: any): Promise<ApiResponse<T>> => {
  const cacheKey = createCacheKey(method, url, undefined, data);
  
  // Check if there's already a pending request for this exact same call
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }
  
  const timestamp = new Date().toISOString();
  const fullUrl = normalizeUrl(getApiBaseUrl(), url);
  const headers = getAuthHeaders();
  
  // Set Content-Type only for non-FormData requests
  if (data && !(data instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (DEBUG) console.debug('[API]', method, url);
  
  const requestPromise = (async () => {
    try {
      const response = await fetchWithTimeout(fullUrl, {
        method,
        headers,
        body: data instanceof FormData ? data : data ? JSON.stringify(data) : undefined,
      });
      
      // Handle 204 No Content responses (empty body)
      let responseData = null;
      if (response.status !== 204) {
        const contentType = response.headers.get('content-type');
        
        // Read response as text first (can only read once)
        const text = await response.text();
        
        // Only parse if we have content
        if (text && text.trim()) {
          if (contentType && contentType.includes('application/json')) {
            try {
              responseData = JSON.parse(text);
            } catch (parseError) {
              if (DEBUG) console.warn('Failed to parse JSON:', parseError);
              responseData = text;
            }
          } else {
            responseData = text;
          }
        }
      }
      
      if (DEBUG) console.debug('[API]', method, url, response.status);
      
      if (!response.ok) {
        throw {
          status: response.status,
          statusText: response.statusText,
          data: responseData,
          message: `Request failed with status code ${response.status}`,
          url,
        };
      }

      invalidateGetCache();
      
      return {
        data: responseData as T,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      };
    } catch (error) {
      handleApiError(error, timestamp);
      throw error;
    } finally {
      // Remove from cache when request completes (success or failure)
      pendingRequests.delete(cacheKey);
    }
  })();
  
  // Store the promise in cache
  pendingRequests.set(cacheKey, requestPromise);
  
  return requestPromise;
};

// Helper function to handle API errors
function handleApiError(error: any, timestamp: string): void {
  let userMessage = '';

  if (error.status) {
    // HTTP error with response
    if (DEBUG) {
      console.error(`[${timestamp}] API Error:`, {
        status: error.status,
        statusText: error.statusText,
        data: error.data,
        message: error.message
      });
    }

    if (error.status === 401) {
      const requestUrl = String(error.url || '');
      const isAuthAttempt = /auth\/(login|register)/.test(requestUrl);
      if (!isAuthAttempt && !isRedirecting) {
        isRedirecting = true;
        notifyUnauthorized();
        if (!window.location.pathname.includes('/login')) {
          window.location.href = loginHrefForCurrentLocation();
        }
      }
      return;
    }

    // Show global toast only for server/network errors (callers typically handle 4xx)
    const serverMessage = error.data?.message || error.data?.error;
    if (error.status >= 500) {
      userMessage = getTenantSubdomain()
        ? ''
        : serverMessage || 'Server error. Please try again later.';
    }
  } else if (error.name === 'AbortError') {
    error.message = getTenantSubdomain()
      ? 'Your instance is starting. Please try again.'
      : 'Request timed out: Please try again';
    userMessage = getTenantSubdomain() ? '' : error.message;
  } else if (typeof navigator !== 'undefined' && !navigator.onLine) {
    error.message = 'Network error: Please check your connection';
    userMessage = error.message;
  } else if (error.message && /fetch|network|connection|timed out/i.test(error.message)) {
    if (getTenantSubdomain()) {
      error.message = 'Your instance is starting. Please try again.';
      userMessage = '';
    } else {
      userMessage = 'Network error: Please check your connection';
    }
  }

  if (userMessage && typeof window !== 'undefined') {
    showErrorToast(userMessage);
  }
}

export default api; 